import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCompany } from "@/hooks/use-active-company";
import { usePricingRole } from "@/hooks/use-pricing-role";
import { formatCurrency, formatDate, formatQuantity } from "@/lib/format";

type Search = { from?: string; to?: string };

export const Route = createFileRoute("/_authenticated/invoices/arkiv")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    from: typeof search.from === "string" ? search.from : undefined,
    to: typeof search.to === "string" ? search.to : undefined,
  }),
  head: () => ({ meta: [
    { title: "PDF-arkiv faktura | VERQO" },
    { name: "description", content: "Samlet utskrift av alle utstedte fakturaer i valgt periode." },
    { property: "og:title", content: "PDF-arkiv faktura | VERQO" },
    { property: "og:description", content: "Samlet utskrift av alle utstedte fakturaer i valgt periode." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: InvoiceArchive,
});

function InvoiceArchive() {
  const { from, to } = Route.useSearch();
  const { companyId } = useActiveCompany();
  const { canManagePricing, isLoading: roleLoading } = usePricingRole();

  const query = useQuery({
    queryKey: ["invoice-archive", companyId, from, to],
    enabled: Boolean(companyId) && canManagePricing,
    queryFn: async () => {
      let request = supabase
        .from("invoice_document_header_v")
        .select("invoice_id,invoice_number,document_type,credit_of_invoice_id,title,description,presentation,reference_name,issue_date,issued_at,delivery_date,due_date,payment_terms_days,subtotal_excl_vat,vat_amount,total_incl_vat,vat_percent,bill_to_name,bill_to_org_number,bill_to_address,bill_to_postal_code,bill_to_city,site_address,site_postal_code,site_city,site_contact_name,site_contact_phone,terms_payment,terms_included,terms_excluded,terms_reservations,seller_name,seller_org_number,seller_address,seller_postal_code,seller_city,seller_phone,seller_email,seller_bank_account,seller_iban,seller_vat_registered,seller_logo_storage_path")
        .eq("company_id", companyId)
        .not("issued_at", "is", null)
        .order("invoice_number");
      if (from) request = request.gte("issue_date", from);
      if (to) request = request.lte("issue_date", to);
      const { data: invoices, error } = await request;
      if (error) throw error;
      const ids = (invoices ?? []).flatMap((invoice) => (invoice.invoice_id ? [invoice.invoice_id] : []));
      const { data: lines, error: lineError } = ids.length
        ? await supabase.from("invoice_document_line_v").select("line_id,invoice_id,description,quantity,unit,unit_price,line_total_excl_vat,sort_order").in("invoice_id", ids).order("sort_order")
        : { data: [], error: null };
      if (lineError) throw lineError;
      return { invoices: invoices ?? [], lines: lines ?? [] };
    },
  });

  if (roleLoading) return <div className="p-10 text-center">Laster…</div>;
  if (!canManagePricing) return <div className="p-10 text-center">Ingen tilgang.</div>;
  if (!query.data) return <div className="p-10 text-center">Laster…</div>;
  const { invoices, lines } = query.data;

  return <>
    <style>{`@page{size:A4;margin:18mm}@media print{.no-print{display:none!important}body{background:white!important}}`}</style>
    <div className="no-print sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b bg-card p-3">
      <Button asChild variant="ghost"><Link to="/invoices"><ArrowLeft className="h-4 w-4"/>Tilbake</Link></Button>
      <div className="text-sm text-muted-foreground">{invoices.length} dokumenter{from || to ? ` · ${from ?? "start"} – ${to ?? "i dag"}` : ""}</div>
      <Button onClick={() => window.print()} disabled={invoices.length === 0}><Printer className="h-4 w-4"/>Skriv ut / lagre som PDF</Button>
    </div>
    {invoices.length === 0 && <p className="p-10 text-center text-muted-foreground">Ingen utstedte dokumenter i perioden.</p>}
    {invoices.map((invoice) => (
      <article key={invoice.invoice_id} className="mx-auto my-8 max-w-[210mm] break-after-page bg-white p-12 text-neutral-900 shadow-xl print:my-0 print:shadow-none">
        <header className="flex justify-between gap-8 border-b-2 border-neutral-900 pb-8">
          <div>
            {invoice.seller_logo_storage_path?.startsWith("http") && <img src={invoice.seller_logo_storage_path} alt={invoice.seller_name ?? ""} className="mb-4 max-h-20 max-w-52 object-contain"/>}
            <div className="font-semibold">{invoice.seller_name}</div>
            <div className="text-sm text-neutral-600">Org.nr {invoice.seller_org_number}{invoice.seller_vat_registered ? " MVA" : ""}</div>
            <div className="text-sm text-neutral-600">{invoice.seller_address}</div>
            <div className="text-sm text-neutral-600">{invoice.seller_postal_code} {invoice.seller_city}</div>
            <div className="text-sm text-neutral-600">Telefon: {invoice.seller_phone}</div>
            <div className="text-sm text-neutral-600">{invoice.seller_email}</div>
          </div>
          <div className="text-right">
            <h1 className="text-3xl font-semibold">{invoice.document_type === "credit_note" ? "KREDITNOTA" : "FAKTURA"}</h1>
            <div className="mt-3 text-sm">Fakturanr: <b>{invoice.invoice_number}</b></div>
            <div className="text-sm">Fakturadato: {formatDate(invoice.issue_date)}</div>
            <div className="text-sm">Forfallsdato: {formatDate(invoice.due_date)}</div>
            {invoice.reference_name && <div className="text-sm">Vår ref: {invoice.reference_name}</div>}
          </div>
        </header>
        <section className="grid grid-cols-2 gap-10 py-8">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase text-neutral-500">Fakturamottaker</div>
            <div className="font-semibold">{invoice.bill_to_name}</div>
            {invoice.bill_to_org_number && <div className="text-sm">Org.nr {invoice.bill_to_org_number}</div>}
            <div className="text-sm">{invoice.bill_to_address}</div>
            <div className="text-sm">{invoice.bill_to_postal_code} {invoice.bill_to_city}</div>
          </div>
          <div>
            <div className="mb-2 text-xs font-semibold uppercase text-neutral-500">Arbeidssted</div>
            <div className="text-sm">{invoice.site_address || "—"}</div>
            <div className="text-sm">{invoice.site_postal_code} {invoice.site_city}</div>
          </div>
        </section>
        {invoice.description && <p className="mb-8 whitespace-pre-wrap text-sm">{invoice.description}</p>}
        <table className="w-full text-sm">
          <thead><tr className="border-b border-neutral-900 text-left"><th className="py-2">Post / beskrivelse</th><th className="text-right">Antall</th><th className="text-right">Enhet</th><th className="text-right">Pris</th><th className="text-right">Sum</th></tr></thead>
          <tbody>
            {lines.filter((line) => line.invoice_id === invoice.invoice_id).map((line, index) => (
              <tr key={index} className="border-b border-neutral-200">
                <td className="py-2">{line.description}</td>
                <td className="text-right">{formatQuantity(Number(line.quantity), line.unit ?? "")}</td>
                <td className="text-right">{line.unit}</td>
                <td className="text-right">{formatCurrency(Number(line.unit_price))}</td>
                <td className="text-right">{formatCurrency(Number(line.line_total_excl_vat))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <section className="ml-auto mt-8 w-80 text-sm">
          <div className="flex justify-between py-1"><span>Sum eks. mva</span><span>{formatCurrency(Number(invoice.subtotal_excl_vat))}</span></div>
          <div className="flex justify-between py-1"><span>MVA {invoice.vat_percent} %</span><span>{formatCurrency(Number(invoice.vat_amount))}</span></div>
          <div className="my-2 border-t border-neutral-900"/>
          <div className="flex justify-between py-1 text-lg font-semibold"><span>Å betale</span><span>{formatCurrency(Number(invoice.total_incl_vat))}</span></div>
        </section>
        {invoice.document_type === "invoice" && (
          <section className="mt-10 border-t pt-6 text-sm">
            <div className="mb-2 text-xs font-semibold uppercase text-neutral-500">Betaling</div>
            <div>Kontonummer: <b>{invoice.seller_bank_account || "Ikke oppgitt"}</b>{invoice.seller_iban ? ` · IBAN ${invoice.seller_iban}` : ""}</div>
            <div>Merk betalingen med fakturanummer {invoice.invoice_number}.</div>
          </section>
        )}
      </article>
    ))}
  </>;
}
